import { Environment, Lightformer } from '@react-three/drei';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { type ReactNode, Suspense, useEffect, useRef } from 'react';
import { StaticBadge } from './StaticBadge';
import type { ExportArtItem } from './types';

export const SOCIAL_IMAGE_WIDTH = 1080;
export const SOCIAL_IMAGE_HEIGHT = 1350;

type CredentialPngRendererProps = {
  face: ReactNode;
  art?: ExportArtItem[];
  requestId: number;
  onCapture: (dataUrl: string) => void;
  onError: (message: string) => void;
};

type CredentialCaptureProps = Omit<CredentialPngRendererProps, 'face'>;

// Frames to let the card's RenderTexture draw before reading the canvas. The scene is
// static and everything it loads sits behind <Suspense>, so this only has to cover the
// texture upload, not any settling.
const SETTLE_FRAMES = 12;

export function CredentialPngRenderer({
  face,
  art,
  requestId,
  onCapture,
  onError,
}: CredentialPngRendererProps) {
  if (requestId === 0) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-[-9999px] opacity-0"
      style={{ width: SOCIAL_IMAGE_WIDTH, height: SOCIAL_IMAGE_HEIGHT }}
    >
      <Canvas
        camera={{ position: [0, 0.15, 8.6], fov: 21 }}
        dpr={1}
        gl={{ alpha: true, preserveDrawingBuffer: true }}
        style={{
          width: SOCIAL_IMAGE_WIDTH,
          height: SOCIAL_IMAGE_HEIGHT,
          background: 'transparent',
        }}
      >
        <ambientLight intensity={Math.PI} />
        <Suspense fallback={null}>
          <group position={[0, 0.25, 0]}>
            <StaticBadge face={face} />
          </group>
          <CredentialCapture
            art={art}
            requestId={requestId}
            onCapture={onCapture}
            onError={onError}
          />
        </Suspense>
        <Environment blur={0.75}>
          <Lightformer
            intensity={2}
            color="white"
            position={[0, -1, 5]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[-1, -1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={3}
            color="white"
            position={[1, 1, 1]}
            rotation={[0, 0, Math.PI / 3]}
            scale={[100, 0.1, 1]}
          />
          <Lightformer
            intensity={10}
            color="white"
            position={[-10, 0, 14]}
            rotation={[0, Math.PI / 2, Math.PI / 3]}
            scale={[100, 10, 1]}
          />
        </Environment>
      </Canvas>
    </div>
  );
}

function CredentialCapture({
  art,
  requestId,
  onCapture,
  onError,
}: CredentialCaptureProps) {
  const { gl } = useThree();
  const frameCountRef = useRef(0);
  const capturedRequestRef = useRef(0);

  useEffect(() => {
    frameCountRef.current = 0;
  }, [requestId]);

  useFrame(() => {
    if (capturedRequestRef.current === requestId) return;

    frameCountRef.current += 1;
    if (frameCountRef.current < SETTLE_FRAMES) return;

    capturedRequestRef.current = requestId;

    // The canvas is read in the frame it was drawn in (preserveDrawingBuffer keeps it
    // either way); the art is fetched, so composing is async.
    composeSocialImage(gl.domElement, art ?? [])
      .then(onCapture)
      .catch((error) => {
        onError(
          error instanceof Error
            ? error.message
            : 'No se pudo generar la imagen.',
        );
      });
  });

  return null;
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    image.src = src;
  });
}

async function composeSocialImage(
  credentialCanvas: HTMLCanvasElement,
  art: ExportArtItem[],
) {
  const images = await Promise.all(art.map((item) => loadImage(item.src)));

  const canvas = document.createElement('canvas');
  canvas.width = SOCIAL_IMAGE_WIDTH;
  canvas.height = SOCIAL_IMAGE_HEIGHT;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo preparar el canvas de exportación.');

  ctx.fillStyle = '#050505';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const gradient = ctx.createRadialGradient(540, 520, 40, 540, 520, 880);
  gradient.addColorStop(0, 'rgba(82, 82, 82, 0.5)');
  gradient.addColorStop(0.48, 'rgba(38, 38, 38, 0.42)');
  gradient.addColorStop(1, 'rgba(5, 5, 5, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  art.forEach((item, index) => {
    const image = images[index];
    const height = (item.width * image.naturalHeight) / image.naturalWidth;
    ctx.save();
    ctx.globalAlpha = item.opacity ?? 1;
    ctx.translate(item.x, item.y);
    ctx.rotate((item.rotate * Math.PI) / 180);
    ctx.drawImage(image, -item.width / 2, -height / 2, item.width, height);
    ctx.restore();
  });

  ctx.drawImage(credentialCanvas, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/png');
}
